import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, CircleAlert, Plus, CircleCheck, ExternalLink, LoaderCircle, Terminal, TriangleAlert, WifiOff, X } from "lucide-react";
import { SECRET_PATHS, supportsPath, type ConfigSnapshot } from "@cg/protocol";
import { api, ApiError, type Install, type ServerConfig } from "../api.ts";
import { Shell } from "../components/Shell.tsx";
import { StatusDot } from "../components/Badge.tsx";
import { Choice, CountryPicker, Row, Section, SecretField, Segmented, Switch, TagInput, type SecretEdit } from "../components/Form.tsx";
import { ago, configErrorHint, num, platformName, serverName, versionAtLeast } from "../format.ts";
import { failoverOrder, fixedProviders, providerBody, providersFor, usesFailover } from "../providers.ts";
import { sourceKey } from "../sources.ts";
import { SIMULATED_PATHS, simulate, type SimulationResult } from "../simulate.ts";
import { useHistory } from "../history.ts";
import { usePlayerNames } from "../players.ts";
import type { RegisterEvent } from "../api.ts";

type Values = Record<string, boolean | number | string | string[]>;
const INTEL = "provider.local.connectionguard-intel.enabled";
const INTEL_RELAY = "provider.local.connectionguard-intel.relay";
type Secrets = Record<string, SecretEdit>;
type SecretState = { set: boolean; hint: string | null };


const FAILURE: { value: "OPEN" | "CLOSED" | "OBSERVE"; title: string; body: string }[] = [
  { value: "OPEN", title: "Let in", body: "Recommended. Players are not locked out while a service is down." },
  { value: "CLOSED", title: "Refuse", body: "Strict. Nobody new gets in while lookups fail." },
  { value: "OBSERVE", title: "Let in and log", body: "Players get in, and the decision is marked for review." },
];

const isSecretState = (v: unknown): v is SecretState => typeof v === "object" && v !== null && !Array.isArray(v) && "set" in v;

/** Effective values as the form's starting point; secrets are kept apart. */
function initialValues(snapshot: ConfigSnapshot): Values {
  const out: Values = {};
  for (const [k, v] of Object.entries(snapshot)) if (!isSecretState(v)) out[k] = v as Values[string];
  return out;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Servers from 0.6 apply dashboard changes at once with this console command. */
const canSyncNow = (install: Install) => Boolean(install.status?.capabilities?.includes("sync_command"));
function SyncHint({ install }: { install: Install }) {
  if (!canSyncNow(install)) return null;
  return (
    <p className="mt-1.5 flex items-center gap-1.5 text-[0.8125rem] text-fg-2">
      <Terminal aria-hidden className="size-3.5 shrink-0 text-fg-3" />
      In a hurry? Run <code className="mono rounded border border-line bg-surface px-1.5 py-0.5 text-[0.75rem] text-fg">/cg cloud sync</code> in the console to apply it right away.
    </p>
  );
}

function ApplyStatus({ cfg, server, install }: { cfg: ServerConfig; server: string; install: Install }) {
  if (cfg.error) {
    return (
      <div role="alert" className="flex gap-3 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3">
        <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-danger" />
        <div className="min-w-0">
          <p className="font-medium text-danger-text">{server} rejected the last change</p>
          <p className="mt-0.5 text-[0.8125rem] text-fg-2">{cfg.error.message} Its previous settings are still active.</p>
          <ErrorHint message={cfg.error.message} />
        </div>
      </div>
    );
  }
  if (cfg.pending) {
    return (
      <div role="status" className="flex gap-3 rounded-lg border border-line bg-subtle px-4 py-3">
        {cfg.online ? <LoaderCircle aria-hidden className="mt-0.5 size-4 shrink-0 animate-spin text-fg-2 motion-reduce:animate-none" /> : <WifiOff aria-hidden className="mt-0.5 size-4 shrink-0 text-warn" />}
        <div>
          <p className="font-medium">{cfg.online ? `Waiting for ${server} to apply your changes…` : `${server} is offline`}</p>
          <p className="mt-0.5 text-[0.8125rem] text-fg-2">{cfg.online ? "While this page is open, the server checks in every few seconds. No restart needed." : "Your changes are saved and apply as soon as it reconnects."}</p>
          {cfg.online && <SyncHint install={install} />}
        </div>
      </div>
    );
  }
  if (cfg.desired && !cfg.desired.reset && cfg.managed.length > 0) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-line px-4 py-3 text-[0.8125rem] text-fg-2">
        <CircleCheck aria-hidden className="size-4 shrink-0 text-accent" />
        <span>Applied on {server}{cfg.desired.updated_by ? `, last changed by ${cfg.desired.updated_by}` : ""} {ago(cfg.desired.updated_at)}. {cfg.managed.length} {cfg.managed.length === 1 ? "setting comes" : "settings come"} from the dashboard, the rest from config.yml.</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-lg border border-line px-4 py-3 text-[0.8125rem] text-fg-2">
      <CircleCheck aria-hidden className="size-4 shrink-0 text-fg-3" />
      <span>All settings currently come from config.yml on {server}. Changes you save here take priority over it.</span>
    </div>
  );
}

function ErrorHint({ message }: { message: string | null | undefined }) {
  const hint = configErrorHint(message);
  if (!hint) return null;
  return (
    <div className="mt-2 text-[0.8125rem] text-fg-2">
      <p>{hint.text}</p>
      {hint.command && <pre className="mono mt-2 overflow-x-auto rounded-md border border-line bg-surface px-3 py-2 text-[0.75rem]">{hint.command}</pre>}
    </div>
  );
}

/** The page's sections, in order. "advanced" only appears when there is something to reset. */
type SectionId = "mode" | "vpn" | "countries" | "actions" | "exemptions" | "advanced";
const SECTION_LABEL: Record<SectionId, string> = {
  mode: "Protection", vpn: "Detection", countries: "Countries", actions: "When flagged", exemptions: "Exemptions", advanced: "Advanced",
};
/** Which section a setting (or secret) lives in, so the navigation can mark unsaved changes and errors. */
function sectionOf(path: string): SectionId {
  if (path === "operation.mode") return "mode";
  if (/^behavior\.(vpn|geo)\.exemptions$/.test(path)) return "exemptions";
  if (/^behavior\.(vpn|geo)\.(kick-player|notify-staff|send-webhook)/.test(path)) return "actions";
  if (path.startsWith("behavior.geo.") || path === "provider.geo.service" || path === "failure-policy.geo" || path === "provider.cache.expiration.geo") return "countries";
  return "vpn";
}

/** The section nearest the top of the window, for the navigation's highlight. */
function useScrollSpy(ids: string[]) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const els = ids.map((id) => document.getElementById(id)).filter((e): e is HTMLElement => e !== null);
    if (!els.length) return;
    const update = () => {
      // The last section whose top has passed a line a third down the window; at the very bottom, the last one.
      const line = window.innerHeight / 3;
      let current = els[0]!.id;
      for (const el of els) if (el.getBoundingClientRect().top <= line) current = el.id;
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) current = els.at(-1)!.id;
      setActive(current);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { window.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [ids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  return active;
}

const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });

/** Section navigation: a column that stays in view on wide screens, a row of chips on phones. */
function SectionNav({ ids, active, marks, desktopOnly, mobileOnly }: { ids: SectionId[]; active: string | undefined; marks: Partial<Record<SectionId, "dirty" | "error">>; desktopOnly?: boolean; mobileOnly?: boolean }) {
  const dot = (id: SectionId) => marks[id] && <span aria-label={marks[id] === "error" ? "needs a fix" : "unsaved changes"} className={`size-1.5 shrink-0 rounded-full ${marks[id] === "error" ? "bg-danger" : "bg-warn"}`} />;
  return (
    <>
      {!mobileOnly && <nav aria-label="Settings sections" className="hidden lg:block">
        <ul className="sticky top-28 space-y-0.5 text-[0.8125rem]">
          {ids.map((id) => (
            <li key={id}>
              <a href={`#${id}`} onClick={(e) => { e.preventDefault(); jump(id); }} aria-current={active === id ? "location" : undefined}
                className={`flex items-center justify-between gap-2 rounded-md px-3 py-1.5 no-underline transition-colors ${active === id ? "bg-subtle font-medium text-fg" : "text-fg-2 hover:bg-subtle hover:text-fg"}`}>
                {SECTION_LABEL[id]}{dot(id)}
              </a>
            </li>
          ))}
        </ul>
      </nav>}
      {!desktopOnly && <nav aria-label="Settings sections" className="-mx-4 mt-5 overflow-x-auto px-4 lg:hidden">
        <ul className="flex gap-2 text-[0.8125rem]">
          {ids.map((id) => (
            <li key={id}>
              <a href={`#${id}`} onClick={(e) => { e.preventDefault(); jump(id); }}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-surface px-3 py-1 text-fg-2 no-underline hover:text-fg">
                {SECTION_LABEL[id]}{dot(id)}
              </a>
            </li>
          ))}
        </ul>
      </nav>}
    </>
  );
}

/** Expert settings of a section, folded away until needed; opens by itself when one of them is changed or wrong. */
function Advanced({ open: mustOpen, summary, children }: { open?: boolean; summary?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const shown = open || Boolean(mustOpen);
  return (
    <div>
      <button type="button" aria-expanded={shown} onClick={() => setOpen(!shown)}
        className="flex w-full items-center gap-2 px-6 py-3 text-left text-[0.8125rem] transition-colors hover:bg-subtle">
        <ChevronRight aria-hidden className={`size-4 shrink-0 text-fg-3 transition-transform duration-150 ${shown ? "rotate-90" : ""}`} />
        <span className="font-medium text-fg-2">Advanced</span>
        {!shown && summary && <span className="min-w-0 truncate text-fg-3">{summary}</span>}
      </button>
      {shown && <div className="divide-y divide-line border-t border-line">{children}</div>}
    </div>
  );
}

/** A row of the detection list. Opens by itself when it has an error. */
function ServiceRow({ index, name, badge, badgeTone, managed, error, move, control, children }: {
  index?: number | undefined; name: string; badge?: string | undefined; badgeTone?: "danger" | undefined; managed?: boolean; error?: string | undefined;
  move?: ReactNode; control?: ReactNode; children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const shown = (open || Boolean(error)) && Boolean(children);
  return (
    <li>
      <div className="flex items-center gap-3 px-6 py-3">
        {index !== undefined && <span className="num w-4 shrink-0 text-[0.8125rem] text-fg-3">{index}</span>}
        <button type="button" aria-expanded={children ? shown : undefined} disabled={!children} onClick={() => setOpen(!shown)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-default">
          {children ? <ChevronRight aria-hidden className={`size-4 shrink-0 text-fg-3 transition-transform duration-150 ${shown ? "rotate-90" : ""}`} /> : <span className="w-4 shrink-0" />}
          <span className="truncate font-medium">{name}</span>
          {managed && <span className="badge badge-neutral h-5 shrink-0 text-[0.6875rem]" title="Set in the dashboard; overrides config.yml">Dashboard</span>}
          {badge && <span className={`hidden truncate text-[0.75rem] sm:inline ${badgeTone === "danger" ? "text-danger-text" : "text-fg-3"}`}>{badge}</span>}
        </button>
        {move}
        {control}
      </div>
      {shown && (
        <div className="space-y-3 px-6 pb-4 pl-[3.25rem] text-[0.8125rem] leading-relaxed">
          {badge && <p className={`sm:hidden ${badgeTone === "danger" ? "text-danger-text" : "text-fg-3"}`}>{badge}</p>}
          {children}
          {error && <p role="alert" className="text-danger-text">{error}</p>}
        </div>
      )}
    </li>
  );
}

/** The switched-off services, folded under one line. */
function MoreServices({ count, open: mustOpen, children }: { count: number; open?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const shown = open || Boolean(mustOpen);
  return (
    <div className="border-t border-line">
      <button type="button" aria-expanded={shown} onClick={() => setOpen(!shown)} className="flex w-full items-center gap-2 px-6 py-3 text-left text-[0.8125rem] text-fg-2 transition-colors hover:bg-subtle hover:text-fg">
        {shown ? <ChevronDown aria-hidden className="size-4 text-fg-3" /> : <Plus aria-hidden className="size-4 text-fg-3" />}
        {shown ? "Switched-off services" : `${count} more ${count === 1 ? "service" : "services"}, switched off`}
      </button>
      {shown && <div className="border-t border-line">{children}</div>}
    </div>
  );
}

function ServerPicker({ networkId, installs }: { networkId: string; installs: Install[] }) {
  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-2xl font-semibold tracking-[-0.025em]">Settings</h1>
      <p className="mt-1 text-fg-2">Choose the server to configure. You can apply the result to all servers when saving.</p>
      <ul className="card mt-6 divide-y divide-line overflow-hidden">
        {installs.map((i) => (
          <li key={i.id}>
            <Link to="/n/$networkId/settings" params={{ networkId }} search={{ server: i.id }} className="flex items-center gap-3 px-5 py-4 no-underline transition-colors hover:bg-subtle">
              <StatusDot tone={i.online ? "ok" : "danger"} />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{serverName(i)}</p>
                <p className="truncate text-[0.8125rem] text-fg-3">{platformName[i.platform]} · {i.status?.mode === "ENFORCE" ? "Enforcing" : i.status?.mode === "OBSERVE" ? "Observing" : "No report yet"}</p>
              </div>
              <ChevronRight aria-hidden className="size-4 text-fg-3" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SettingsPage() {
  const { networkId } = useParams({ from: "/n/$networkId/settings" });
  const { server } = useSearch({ from: "/n/$networkId/settings" });
  const net = useQuery({ queryKey: ["network", networkId], queryFn: () => api.network(networkId) });
  const installs = net.data?.installs ?? [];
  const installId = server ?? (installs.length === 1 ? installs[0]!.id : undefined);
  const install = installs.find((i) => i.id === installId);

  if (net.isPending) return <Shell networkId={networkId}><div className="skeleton h-64" /></Shell>;
  if (!installId || !install) return <Shell networkId={networkId}><ServerPicker networkId={networkId} installs={installs} /></Shell>;
  return <Shell networkId={networkId}><ServerSettings key={installId} networkId={networkId} install={install} serverCount={installs.length} /></Shell>;
}

function ServerSettings({ networkId, install, serverCount }: { networkId: string; install: Install; serverCount: number }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const cfgQ = useQuery({
    queryKey: ["config", install.id],
    queryFn: () => api.serverConfig(install.id),
    refetchInterval: (q) => (q.state.data?.pending ? 3000 : 30_000),
  });
  const stats = useQuery({ queryKey: ["stats", networkId, "24h", install.id], queryFn: () => api.stats(networkId, "24h", install.id) });
  const cfg = cfgQ.data;
  const effective = cfg?.effective ?? null;

  const base = useMemo(() => (effective ? initialValues(effective) : null), [effective]);
  const [values, setValues] = useState<Values | null>(null);
  const [secrets, setSecrets] = useState<Secrets>({});
  const [applyTo, setApplyTo] = useState<"server" | "network">("server");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  // The change just saved, so the bottom bar can follow it until the server confirms or refuses.
  const [lastSave, setLastSave] = useState<{ version: number; at: number; others: number; reset?: boolean; skipped?: number } | null>(null);
  const [now, setNow] = useState(Date.now());
  // "Block selected" with no country picked yet still shows the picker.
  const [blockOpen, setBlockOpen] = useState(false);
  const saveVersion = lastSave?.version ?? 0;
  const saveRejected = Boolean(lastSave && cfgQ.data?.error?.version === saveVersion);
  const saveApplied = Boolean(lastSave && !saveRejected && (cfgQ.data?.applied_version ?? 0) >= saveVersion && !cfgQ.data?.pending);
  useEffect(() => {
    if (!lastSave || saveApplied || saveRejected) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [lastSave, saveApplied, saveRejected]);
  useEffect(() => {
    // Applied: the server's report is the truth again. Refused: the form keeps the edits, shown as unsaved, to fix and retry.
    if (saveApplied || saveRejected) setSavedValues(null);
  }, [saveApplied, saveRejected]);
  useEffect(() => {
    if (!saveApplied) return;
    const t = window.setTimeout(() => setLastSave(null), 6000);
    return () => window.clearTimeout(t);
  }, [saveApplied]);
  // After saving, compare against what was saved, not the server's report: until the server has
  // applied it, its report still shows the old values and the form would look unsaved.
  const [savedValues, setSavedValues] = useState<Values | null>(null);
  const compareTo = savedValues ?? base;
  const dirtyFields = compareTo && values ? Object.keys(values).filter((k) => !same(values[k], compareTo[k])) : [];
  const dirtySecrets = Object.entries(secrets).filter(([, e]) => e.mode !== "keep" && !(e.mode === "set" && e.value === ""));
  const dirty = dirtyFields.length > 0 || dirtySecrets.length > 0;
  // Adopt fresh server values unless the user is mid-edit or a save is still on its way.
  useEffect(() => { if (base && !dirty && !savedValues) setValues(base); }, [base]); // eslint-disable-line react-hooks/exhaustive-deps

  // What-if: replay the last 7 days of this server's decisions against the edited settings.
  const simRelevant = dirtyFields.some((f) => SIMULATED_PATHS.includes(f));
  const history = useHistory(networkId, { install: install.id, enabled: simRelevant });
  const simulation = useMemo(() => (simRelevant && history.data && values ? simulate(history.data.events, values) : null), [simRelevant, history.data, values]);

  const name = serverName(install);
  if (cfgQ.isPending) return <div className="skeleton h-64" />;
  if (cfgQ.error) return <p role="alert" className="text-danger-text">Settings could not be loaded. They retry automatically.</p>;
  if (!cfg) return null;
  const canEdit = cfg.role !== "viewer";

  if (!effective || !values) {
    return (
      <div className="mx-auto max-w-2xl">
        <h1 className="text-2xl font-semibold tracking-[-0.025em]">Settings</h1>
        <div className="card mt-6 px-6 py-8 text-center">
          <p className="font-medium">Waiting for {name} to report its settings</p>
          <p className="mx-auto mt-1 max-w-md text-fg-2">This happens on its next sync. If it does not show up, the server runs a Connection Guard version without dashboard settings; update it to 0.5 or newer.</p>
        </div>
      </div>
    );
  }

  const v = <T,>(path: string) => values[path] as T;
  const set = (path: string, value: Values[string]) => setValues({ ...values, [path]: value });
  const managed = (path: string) => cfg.managed.includes(path) || dirtyFields.includes(path);
  const secretState = (path: string) => effective[path] as SecretState | undefined;
  const secretEdit = (path: string): SecretEdit => secrets[path] ?? { mode: "keep" };
  const hasKey = (path: string) => { const e = secretEdit(path); return e.mode === "set" ? e.value.length > 0 : e.mode === "clear" ? false : Boolean(secretState(path)?.set); };

  const providers = providersFor(effective);
  const enabledProviders = providers.filter((p) => v<boolean>(`provider.vpn.${p.key}.enabled`));
  // Failover (plugin 0.5.2+) asks one service after another; voting asks all and counts. Older plugins only vote.
  const hasFailover = supportsPath(effective, "provider.vpn-failover.enabled");
  const hasIntel = supportsPath(effective, INTEL);
  // 0.5.2 plugins that do not list the switch yet still use failover unless config.yml turns it off.
  const implicitFailover = !hasFailover && versionAtLeast(install.plugin_version, "0.5.2");
  const failover = usesFailover(values, effective, install.plugin_version);
  const healthKeys = (install.status?.providers ?? []).filter((p) => p.scope === "VPN").map((p) => sourceKey(p.id));
  const fixed = fixedProviders(effective, healthKeys);
  const order = v<string[]>("provider.vpn-failover.order") ?? [];
  const chain = failoverOrder(enabledProviders, order);
  const fullChain = failoverOrder([...enabledProviders, ...fixed], order);
  const moveInChain = (key: string, by: -1 | 1) => {
    const keys: string[] = chain.map((p) => p.key).filter((k) => k !== "ip-api");
    const i = keys.indexOf(key), j = i + by;
    if (i < 0 || j < 0 || j >= keys.length) return;
    [keys[i], keys[j]] = [keys[j]!, keys[i]!];
    set("provider.vpn-failover.order", keys);
  };
  const geoType = v<string>("behavior.geo.type");
  const geoList = v<string[]>("behavior.geo.list") ?? [];
  const geoMode = geoType === "WHITELIST" ? "allow" : geoList.length > 0 || blockOpen ? "block" : "off";
  const observing = v<string>("operation.mode") === "OBSERVE";
  const wouldRefuse = stats.data?.totals.would_refuse ?? 0;

  // Mirrors the plugin's own validation, in words an operator understands.
  const errors: Record<string, string> = {};
  for (const p of providers) if (p.keyRequired && v<boolean>(`provider.vpn.${p.key}.enabled`) && !hasKey(p.keyPath!)) errors[`provider.vpn.${p.key}.enabled`] = `${p.name} needs an API key.`;
  if (!failover && enabledProviders.length > 0 && v<number>("required-positive-flags") > enabledProviders.length) errors["required-positive-flags"] = `Only ${enabledProviders.length} ${enabledProviders.length === 1 ? "provider is" : "providers are"} enabled.`;
  if (geoMode === "allow" && geoList.length === 0) errors["behavior.geo.list"] = "Pick at least one country, or nobody can join.";
  for (const scope of ["vpn", "geo"]) if (v<boolean>(`behavior.${scope}.send-webhook.enabled`) && !hasKey(`behavior.${scope}.send-webhook.url`)) errors[`behavior.${scope}.send-webhook.enabled`] = "Add the Discord webhook URL.";
  const hasErrors = Object.keys(errors).length > 0;

  /** One detection service in the list: name, key status and switch; description, caution and key field fold out. */
  const serviceRow = (p: (typeof providers)[number], index: number | undefined, move: ReactNode) => {
    const path = `provider.vpn.${p.key}.enabled`;
    const on = v<boolean>(path) ?? false;
    const keyed = p.keyPath ? hasKey(p.keyPath) : false;
    const status = !p.keyPath ? "no key needed" : keyed ? "key set" : p.keyRequired ? "key missing" : "free tier, no key";
    return (
      <ServiceRow key={p.key} index={index} name={p.name} managed={managed(path)} error={errors[path]} badge={status}
        badgeTone={p.keyPath && !keyed && p.keyRequired && on ? "danger" : undefined} move={move}
        control={<Switch label={`Use ${p.name}`} checked={on} onChange={(x) => set(path, x)} />}>
        <p className="text-fg-2">{providerBody(p, install.plugin_version, supportsPath(effective, "provider.vpn-failover.order"))}
          {p.signup && <> <a href={p.signup} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-fg-2 hover:text-fg">Website <ExternalLink className="size-3" /></a></>}</p>
        {p.caution && <p className={`flex gap-1.5 ${on ? "text-warn-text" : "text-fg-3"}`}><TriangleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />{p.caution}</p>}
        {p.keyPath && on && (
          <SecretField state={secretState(p.keyPath)} edit={secretEdit(p.keyPath)} onEdit={(e) => setSecrets({ ...secrets, [p.keyPath!]: e })}
            placeholder={`${p.name} API key${p.keyRequired ? "" : " (optional)"}`} />
        )}
      </ServiceRow>
    );
  };

  // Navigation: which sections exist, which hold unsaved changes or errors.
  const showAdvanced = canEdit && cfg.managed.length > 0;
  const sectionIds: SectionId[] = ["mode", "vpn", "countries", "actions", "exemptions", ...(showAdvanced ? ["advanced" as const] : [])];
  const marks: Partial<Record<SectionId, "dirty" | "error">> = {};
  for (const f of [...dirtyFields, ...dirtySecrets.map(([k]) => k)]) marks[sectionOf(f)] = "dirty";
  for (const f of Object.keys(errors)) marks[sectionOf(f)] = "error";
  const inAny = (paths: string[]) => paths.some((p) => dirtyFields.includes(p) || p in errors);
  const vpnAdvanced = ["provider.vpn-failover.enabled", "provider.max-external-attempts", "required-positive-flags", "failure-policy.vpn", "provider.cache.expiration.vpn"];
  const geoAdvanced = ["provider.geo.service", "failure-policy.geo", "provider.cache.expiration.geo"];
  const hours = (path: string) => Math.round((v<number>(path) ?? 60) / 60);
  const policyWord = (path: string) => ({ OPEN: "let in", CLOSED: "refuse", OBSERVE: "let in and log" } as Record<string, string>)[v<string>(path)] ?? "";

  const save = async () => {
    if (!dirty || hasErrors) return;
    setSaving(true); setSaveError(null);
    const keep = new Set([...cfg.managed.filter((p) => !(SECRET_PATHS as readonly string[]).includes(p)), ...dirtyFields]);
    const body = {
      values: Object.fromEntries([...keep].filter((k) => k in values).map((k) => [k, values[k]])),
      secrets: Object.fromEntries(dirtySecrets.map(([k, e]) => [k, e.mode === "set" ? e.value : ""])),
      apply_to: applyTo,
    };
    try {
      const res = await api.saveConfig(install.id, body);
      setSavedValues(values);
      setLastSave({ version: res.versions[install.id] ?? 0, at: Date.now(), others: Object.keys(res.versions).length - 1, skipped: Object.keys(res.skipped ?? {}).length });
      setNow(Date.now());
      setSecrets({});
      await qc.invalidateQueries({ queryKey: ["config"] });
    } catch (err) {
      setSaveError(err instanceof ApiError && err.issues.length ? err.issues.map((i) => `${i.path}: ${i.message}`).join("; ") : "Saving failed. Try again in a moment.");
    } finally { setSaving(false); }
  };
  const reset = async () => {
    setConfirmReset(false);
    const res = await api.resetConfig(install.id);
    setLastSave({ version: res.version, at: Date.now(), others: 0, reset: true });
    setNow(Date.now());
    setSecrets({});
    await qc.invalidateQueries({ queryKey: ["config", install.id] });
  };

  return <SettingsLayout sectionIds={sectionIds} marks={marks}>
    {/* the header, status and summary, then the sections */}
    <div className="pb-24">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.025em]">Settings</h1>
          <p className="mt-1 text-fg-2">
            {name} · {platformName[install.platform]}
            {serverCount > 1 && <> · <button type="button" className="underline decoration-line-strong underline-offset-4 hover:text-fg" onClick={() => navigate({ to: "/n/$networkId/settings", params: { networkId }, search: {} })}>switch server</button></>}
          </p>
        </div>
      </div>
      <SectionNavMobile sectionIds={sectionIds} marks={marks} />
      <div className="mt-6"><ApplyStatus cfg={cfg} server={name} install={install} /></div>
      {!canEdit && <p className="mt-4 text-[0.8125rem] text-fg-2">You can view these settings. Owners and admins can change them.</p>}

      {/* At a glance: the settings as they stand in the form, each a shortcut to its section. */}
      <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {([
          ["mode", "Mode", observing ? "Observe" : "Enforce"],
          ["vpn", "Detection", enabledProviders.length === 0 && !(hasIntel && v<boolean>(INTEL)) ? "Off"
            : [hasIntel && v<boolean>(INTEL) ? "Intel" : "", enabledProviders.length ? `${enabledProviders.length} ${enabledProviders.length === 1 ? "service" : "services"}` : ""].filter(Boolean).join(" + ")],
          ["countries", "Countries", geoMode === "off" ? "Off" : geoMode === "allow" ? `Only ${geoList.length} allowed` : `${geoList.length} blocked`],
          ["actions", "On a hit", observing ? "Log only" : [
            v<boolean>("behavior.vpn.kick-player") ? "Refuse" : "Let in",
            v<boolean>("behavior.vpn.send-webhook.enabled") ? "Discord" : "", v<boolean>("behavior.vpn.notify-staff") ? "staff" : "",
          ].filter(Boolean).join(", ")],
        ] as const).map(([id, label, value]) => (
          <button key={id} type="button" onClick={() => jump(id)} className="rounded-lg bg-subtle px-3 py-2.5 text-left transition-colors hover:bg-line/60">
            <span className="block text-[0.75rem] text-fg-3">{label}</span>
            <span className="mt-0.5 flex items-center gap-1.5 truncate font-medium">{value}{marks[id] && <span className={`size-1.5 shrink-0 rounded-full ${marks[id] === "error" ? "bg-danger" : "bg-warn"}`} />}</span>
          </button>
        ))}
      </div>

      <fieldset disabled={!canEdit || saving} className="mt-6 min-w-0 space-y-6">
        <Section id="mode" title="Protection mode" managed={managed("operation.mode")} description={versionAtLeast(install.plugin_version, "0.6.0") ? "Enforce refuses flagged players; Observe only logs what would have happened." : "Start by observing. Switch to enforce when the decisions look right."}>
          <div className="px-6 py-4">
            <Choice name="Protection mode" value={v<string>("operation.mode") as "OBSERVE" | "ENFORCE"} onChange={(m) => set("operation.mode", m)} options={[
              { value: "OBSERVE", title: "Observe", body: "Check every player and log the result, but let everyone in." },
              { value: "ENFORCE", title: "Enforce", body: "Refuse players who use a VPN or come from a blocked country." },
            ]} />
            {observing && wouldRefuse > 0 && (
              <p className="mt-3 text-[0.8125rem] text-fg-2">In the last 24 hours, <span className="font-medium text-fg">{num(wouldRefuse)}</span> connections would have been refused. <Link to="/n/$networkId/register" params={{ networkId }} search={{ server: install.id }}>Review them</Link></p>
            )}
          </div>
        </Section>

        <Section id="vpn" title="VPN and proxy detection" description={failover
          ? "Enabled services are asked one after another about new IP addresses. Results are cached, so most logins cost no lookup at all."
          : "Each enabled service is asked about new IP addresses. Results are cached, so most logins cost no lookup at all."}>
          {/* One list: Intel, then the services in the order they are asked; switched-off services folded at the end. */}
          <ul className="divide-y divide-line">
            {hasIntel && (
              <ServiceRow name="Connection Guard Intel" managed={managed(INTEL) || managed(INTEL_RELAY)} badge="on your server"
                control={<Switch label="Use Connection Guard Intel" checked={v<boolean>(INTEL) ?? false} onChange={(on) => set(INTEL, on)} />}>
                <p className="text-fg-2">{versionAtLeast(install.plugin_version, "0.6.1")
                  ? "Daily lists of VPN servers, Tor exits, public proxies and privacy relays, checked on your server before any service is asked. Player addresses never leave it."
                  : "Daily lists of VPN servers, Tor exits and privacy relays, checked on your server before any service is asked. Player addresses never leave it."}</p>
                {v<boolean>(INTEL) && supportsPath(effective, INTEL_RELAY) && (
                  <Segmented name="Privacy relays" value={(v<string>(INTEL_RELAY) ?? "ALLOW") as "ALLOW" | "VPN"} onChange={(x) => set(INTEL_RELAY, x)} options={[
                    { value: "ALLOW", title: "Let relays in", body: "Recommended. iCloud Private Relay is Apple's privacy feature for Safari and DNS, not a VPN service." },
                    { value: "VPN", title: "Treat as VPN", body: "Strict. Players behind iCloud Private Relay are refused like VPN users." },
                  ]} />
                )}
              </ServiceRow>
            )}
            {(failover ? fullChain : enabledProviders).map((p, i) => {
              const isFixed = fixed.includes(p);
              const movable = failover && hasFailover && !isFixed && p.key !== "ip-api" && supportsPath(effective, "provider.vpn-failover.order");
              const pos = chain.filter((x) => x.key !== "ip-api").findIndex((x) => x.key === p.key);
              const lastMovable = chain.filter((x) => x.key !== "ip-api").length - 1;
              return isFixed ? (
                <ServiceRow key={p.key} index={failover ? i + 1 : undefined} name={p.name} badge="set in config.yml" />
              ) : serviceRow(p, failover ? i + 1 : undefined, movable ? (
                <span className="flex gap-0.5">
                  <button type="button" className="btn btn-ghost size-7 p-0" aria-label={`Move ${p.name} up`} disabled={pos <= 0} onClick={() => moveInChain(p.key, -1)}><ArrowUp className="size-3.5" /></button>
                  <button type="button" className="btn btn-ghost size-7 p-0" aria-label={`Move ${p.name} down`} disabled={pos >= lastMovable} onClick={() => moveInChain(p.key, 1)}><ArrowDown className="size-3.5" /></button>
                </span>
              ) : p.key === "ip-api" && failover ? <span className="text-[0.75rem] text-fg-3">always last</span> : undefined);
            })}
          </ul>
          {providers.some((p) => !v<boolean>(`provider.vpn.${p.key}.enabled`)) && (
            <MoreServices count={providers.filter((p) => !v<boolean>(`provider.vpn.${p.key}.enabled`)).length}
              open={providers.some((p) => !v<boolean>(`provider.vpn.${p.key}.enabled`) && dirtyFields.includes(`provider.vpn.${p.key}.enabled`))}>
              <ul className="divide-y divide-line">
                {providers.filter((p) => !v<boolean>(`provider.vpn.${p.key}.enabled`)).map((p) => serviceRow(p, undefined, undefined))}
              </ul>
            </MoreServices>
          )}
          {failover && !hasFailover && fullChain.length > 0 && (
            <p className="px-6 py-3 text-[0.75rem] text-fg-3">This plugin version keeps the order in config.yml (provider.vpn-failover.order). Connection Guard Intel and the Tor list are always checked first, IP-API last.</p>
          )}
          <Advanced open={inAny(vpnAdvanced)}
            summary={[failover ? "one after another" : "all at once", `no answer: ${policyWord("failure-policy.vpn")}`, `cache ${hours("provider.cache.expiration.vpn")} h`].join(" · ")}>
            {hasFailover && (
              <Row label="How services are asked" managed={managed("provider.vpn-failover.enabled")}
                help="Applies to every enabled service, with or without a key.">
                <Segmented name="Lookup strategy" value={failover ? "failover" : "voting"} onChange={(x) => set("provider.vpn-failover.enabled", x === "failover")} options={[
                  { value: "failover", title: "One after another", body: "Recommended. Fewer lookups: when a service fails or its daily limit is used up, the next one steps in." },
                  { value: "voting", title: "All at once", body: "Every service votes. More lookups, but you can require agreement." },
                ]} />
              </Row>
            )}
            {hasFailover && failover && supportsPath(effective, "provider.max-external-attempts") && (
              <Row label="Services asked per player" managed={managed("provider.max-external-attempts")}
                help="At most this many services learn a player's address when the first ones fail. 1 sends each address to a single service only.">
                <select className="input w-auto pr-8" value={v<number>("provider.max-external-attempts") ?? 16} onChange={(e) => set("provider.max-external-attempts", Number(e.target.value))}>
                  {[1, 2, 3, 16].map((n) => <option key={n} value={n}>{n === 16 ? "As many as needed" : n === 1 ? "1 (strictest)" : n}</option>)}
                </select>
              </Row>
            )}
            {failover ? (
              v<number>("required-positive-flags") > 1 && (
                <p className="px-6 py-3 text-[0.75rem] text-fg-3">
                  One clear answer decides while services are asked one after another. Your stored vote count of {v<number>("required-positive-flags")} applies again if you switch to all at once.
                </p>
              )
            ) : (
              <Row label="Votes needed" managed={managed("required-positive-flags")} error={errors["required-positive-flags"]}
                help={implicitFailover
                  ? "This server's plugin asks services one after another by default, so one clear answer decides. The vote count only applies if that is switched off in config.yml (provider.vpn-failover.enabled: false)."
                  : "How many enabled services must agree before a player counts as using a VPN."}>
                <select className="input w-auto pr-8" value={v<number>("required-positive-flags")} onChange={(e) => set("required-positive-flags", Number(e.target.value))}>
                  {Array.from({ length: Math.max(1, enabledProviders.length, v<number>("required-positive-flags")) }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n} of {Math.max(enabledProviders.length, 1)}</option>
                  ))}
                </select>
              </Row>
            )}
            <Row label="If no service answers" managed={managed("failure-policy.vpn")} help="Happens when services are down or a daily limit is used up.">
              <Segmented name="VPN failure policy" value={v<string>("failure-policy.vpn") as "OPEN"} onChange={(x) => set("failure-policy.vpn", x)} options={FAILURE} />
            </Row>
            <Row label="Reuse VPN results for" managed={managed("provider.cache.expiration.vpn")} help="How long a result is reused before an address is checked again. Longer saves daily lookups.">
              <CacheHours path="provider.cache.expiration.vpn" value={v<number>("provider.cache.expiration.vpn")} set={set} />
            </Row>
          </Advanced>
        </Section>

        <Section id="countries" title="Country rules" managed={managed("behavior.geo.type") || managed("behavior.geo.list")} description="Block connections from some countries, or allow only a few.">
          <div className="px-6 py-4">
            <div role="group" aria-label="Country rule" className="segmented">
              {([["off", "Off"], ["block", "Block selected"], ["allow", "Allow only selected"]] as const).map(([id, label]) => (
                <button key={id} type="button" aria-pressed={geoMode === id} onClick={() => {
                  setBlockOpen(id === "block");
                  if (id === "off") setValues({ ...values, "behavior.geo.type": "BLACKLIST", "behavior.geo.list": [] });
                  else set("behavior.geo.type", id === "allow" ? "WHITELIST" : "BLACKLIST");
                }}>{label}</button>
              ))}
            </div>
            {(geoMode !== "off" || geoType === "BLACKLIST") && geoMode !== "off" && (
              <div className="mt-4">
                <p className="mb-2 text-[0.8125rem] text-fg-2">{geoMode === "allow" ? "Players from any other country are refused." : "Players from these countries are refused."}</p>
                <CountryPicker selected={geoList} onChange={(list) => set("behavior.geo.list", list)} />
                {errors["behavior.geo.list"] && <p role="alert" className="mt-1.5 text-[0.8125rem] text-danger-text">{errors["behavior.geo.list"]}</p>}
              </div>
            )}
            {geoMode === "off" && (
              <button type="button" className="mt-4 text-[0.8125rem] text-fg-2 underline decoration-line-strong underline-offset-4 hover:text-fg" onClick={() => setBlockOpen(true)}>
                Add a country to block
              </button>
            )}
          </div>
          <Advanced open={inAny(geoAdvanced)}
            summary={[geoMode !== "off" ? `lookup: ${v<string>("provider.geo.service")}` : "", geoMode !== "off" ? `unknown: ${policyWord("failure-policy.geo")}` : "", `cache ${hours("provider.cache.expiration.geo")} h`].filter(Boolean).join(" · ")}>
          {geoMode !== "off" && (
            <>
              <Row label="Country lookup" managed={managed("provider.geo.service")} help="Where the country of an IP address comes from.">
                <select className="input" value={v<string>("provider.geo.service")} onChange={(e) => set("provider.geo.service", e.target.value)}>
                  <option value="IP-API">IP-API (free, no key)</option>
                  <option value="ProxyCheck">ProxyCheck (uses its key above)</option>
                  <option value="Local">Local database (imported on the server)</option>
                  <option value="Disabled">Disabled</option>
                </select>
              </Row>
              <Row label="If the country is unknown" managed={managed("failure-policy.geo")}>
                <Segmented name="Country failure policy" value={v<string>("failure-policy.geo") as "OPEN"} onChange={(x) => set("failure-policy.geo", x)} options={FAILURE} />
              </Row>
            </>
          )}
            <Row label="Reuse country results for" managed={managed("provider.cache.expiration.geo")} help="How long a country is reused before an address is looked up again.">
              <CacheHours path="provider.cache.expiration.geo" value={v<number>("provider.cache.expiration.geo")} set={set} />
            </Row>
          </Advanced>
        </Section>

        <Section id="actions" title="When a player is flagged" description={observing ? "These apply once you switch to Enforce; in Observe mode players are only logged." : undefined}>
          {(["vpn", "geo"] as const).map((scope) => {
            const label = scope === "vpn" ? "VPN or proxy" : "Blocked country";
            return (
              <Row key={scope} label={label} help={scope === "vpn" ? "A player connects through a VPN or proxy." : "A player connects from a country your rules refuse."}
                managed={["kick-player", "notify-staff", "send-webhook.enabled"].some((k) => managed(`behavior.${scope}.${k}`))} error={errors[`behavior.${scope}.send-webhook.enabled`]}>
                <div className="space-y-3">
                  {([["kick-player", "Refuse the connection"], ["notify-staff", "Notify staff in chat"], ["send-webhook.enabled", "Post to Discord"]] as const).map(([key, text]) => (
                    <label key={key} className="flex items-center gap-3">
                      <Switch label={`${label}: ${text}`} checked={v<boolean>(`behavior.${scope}.${key}`) ?? false} onChange={(on) => set(`behavior.${scope}.${key}`, on)} />
                      <span>{text}</span>
                    </label>
                  ))}
                  {v<boolean>(`behavior.${scope}.send-webhook.enabled`) && (
                    <SecretField type="url" state={secretState(`behavior.${scope}.send-webhook.url`)} edit={secretEdit(`behavior.${scope}.send-webhook.url`)}
                      onEdit={(e) => setSecrets({ ...secrets, [`behavior.${scope}.send-webhook.url`]: e })} placeholder="https://discord.com/api/webhooks/…" />
                  )}
                  {v<boolean>(`behavior.${scope}.send-webhook.enabled`) && versionAtLeast(install.plugin_version, "0.6.1") && (
                    <p className="text-[0.8125rem] text-fg-3">
                      Your own message: set <code className="mono">format: TEXT</code> in config.yml and write <code className="mono">messages.{scope}-webhook</code> in the
                      plugin's language file, with any of <span className="mono">%NAME% %UUID% %IS_VPN% %IP% %LOCATION% %COUNTRY% %CITY% %ISP% %TIME%</span>.
                    </p>
                  )}
                </div>
              </Row>
            );
          })}
          <p className="px-6 py-3 text-[0.75rem] text-fg-3">Console commands on a hit (for example bans) can only be set in config.yml, for safety.</p>
        </Section>

        <Section id="exemptions" title="Exemptions" description="Player names or IP addresses that are never checked. Useful for staff who travel or use a work VPN.">
          <Row label="Skip the VPN check" managed={managed("behavior.vpn.exemptions")}>
            <TagInput values={v<string[]>("behavior.vpn.exemptions") ?? []} onChange={(x) => set("behavior.vpn.exemptions", x)} placeholder="Name or IP, then Enter" />
          </Row>
          <Row label="Skip country rules" managed={managed("behavior.geo.exemptions")}>
            <TagInput values={v<string[]>("behavior.geo.exemptions") ?? []} onChange={(x) => set("behavior.geo.exemptions", x)} placeholder="Name or IP, then Enter" />
          </Row>
        </Section>

        {showAdvanced && (
          <section id="advanced" aria-label="Advanced" className="card flex scroll-mt-28 flex-wrap items-center justify-between gap-3 border-danger/30 px-6 py-4">
            <div>
              <p className="font-medium">Use config.yml only</p>
              <p className="text-[0.8125rem] text-fg-2">Removes every dashboard setting from {name}. The file on the server was never changed.</p>
            </div>
            {confirmReset
              ? <div className="flex gap-2"><button type="button" className="btn btn-ghost" onClick={() => setConfirmReset(false)}>Cancel</button><button type="button" className="btn bg-danger text-white hover:brightness-110" onClick={reset}>Remove dashboard settings</button></div>
              : <button type="button" className="btn btn-secondary text-danger-text" onClick={() => setConfirmReset(true)}>Reset to config.yml</button>}
          </section>
        )}
      </fieldset>

      {canEdit && !dirty && lastSave && (
        <div role="status" aria-live="polite" className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/90 backdrop-blur-md">
          <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3 sm:px-6 lg:max-w-5xl lg:pl-[13.5rem]">
            {saveRejected ? (
              <>
                <CircleAlert aria-hidden className="size-5 shrink-0 text-danger" />
                <p className="min-w-0 flex-1 text-[0.8125rem]"><span className="font-medium text-danger-text">{name} refused the change.</span> <span className="text-fg-2">{cfgQ.data?.error?.message} Its previous settings are still active.{configErrorHint(cfgQ.data?.error?.message) ? " See the note at the top." : ""}</span></p>
              </>
            ) : saveApplied ? (
              <>
                <CircleCheck aria-hidden className="size-5 shrink-0 text-accent" />
                <p className="min-w-0 flex-1 text-[0.8125rem]"><span className="font-medium">{lastSave.reset ? `${name} uses config.yml again.` : `Applied on ${name}.`}</span> <span className="text-fg-2">Active now, no restart needed.</span></p>
              </>
            ) : (
              <>
                {cfgQ.data?.online === false
                  ? <WifiOff aria-hidden className="size-5 shrink-0 text-warn" />
                  : <LoaderCircle aria-hidden className="size-5 shrink-0 animate-spin text-fg-2 motion-reduce:animate-none" />}
                <div className="min-w-0 flex-1 text-[0.8125rem]">
                  <p className="font-medium">
                    Saved. {cfgQ.data?.online === false ? `${name} is offline and applies it when it reconnects.` : `Waiting for ${name} to apply it…`}
                    {cfgQ.data?.online !== false && <span className="num ml-1.5 font-normal text-fg-3">{Math.max(0, Math.round((now - lastSave.at) / 1000))} s</span>}
                  </p>
                  <p className="text-fg-2">
                    {cfgQ.data?.online === false ? "Nothing else to do." : "While this page is open, the server checks in every few seconds, so this usually takes about ten seconds."}
                    {lastSave.others > 0 && ` Also sent to ${lastSave.others} other ${lastSave.others === 1 ? "server" : "servers"}.`}
                    {(lastSave.skipped ?? 0) > 0 && ` ${lastSave.skipped} of them ${lastSave.skipped === 1 ? "runs" : "run"} an older Connection Guard and kept ${lastSave.skipped === 1 ? "its" : "their"} own values for the newer settings.`}
                  </p>
                  {cfgQ.data?.online !== false && now - lastSave.at > 8000 && <SyncHint install={install} />}
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-subtle" aria-hidden>
                    <div className="h-full rounded-full bg-accent transition-[width] duration-1000 ease-linear" style={{ width: `${Math.min(95, ((now - lastSave.at) / 30_000) * 100)}%` }} />
                  </div>
                </div>
              </>
            )}
            <button type="button" className="btn btn-ghost size-8 shrink-0 p-0" aria-label="Hide" title="Hide" onClick={() => setLastSave(null)}><X className="size-4" /></button>
          </div>
        </div>
      )}

      {canEdit && dirty && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/90 backdrop-blur-md">
          <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6 lg:max-w-5xl lg:pl-[13.5rem]">
            {simRelevant && !hasErrors && <Impact result={simulation} loading={history.isFetching} />}
            <p className="min-w-0 flex-1 text-[0.8125rem]">
              {hasErrors ? <span className="text-danger-text">Fix the highlighted settings to save.</span>
                : saveError ? <span className="text-danger-text">{saveError}</span>
                : <span className="text-fg-2">{dirtyFields.length + dirtySecrets.length} unsaved {dirtyFields.length + dirtySecrets.length === 1 ? "change" : "changes"}</span>}
            </p>
            {serverCount > 1 && (
              <label className="flex items-center gap-2 text-[0.8125rem] text-fg-2">
                <input type="checkbox" className="size-4 accent-[var(--accent)]" checked={applyTo === "network"} onChange={(e) => setApplyTo(e.target.checked ? "network" : "server")} />
                Apply to all {serverCount} servers
              </label>
            )}
            <button type="button" className="btn btn-ghost" onClick={() => { setValues(compareTo); setSecrets({}); setSaveError(null); }}>Discard</button>
            <button type="button" className="btn btn-primary" disabled={hasErrors || saving} onClick={save}>
              {saving ? <><LoaderCircle aria-hidden className="size-4 animate-spin motion-reduce:animate-none" /> Saving…</> : "Save and apply"}
            </button>
          </div>
        </div>
      )}
    </div>
  </SettingsLayout>;
}

/** Hours input for a cache duration stored in minutes. */
function CacheHours({ path, value, set }: { path: string; value: number | undefined; set: (path: string, value: number) => void }) {
  return (
    <div className="flex items-center gap-2">
      <input type="number" min={1} max={8760} className="input w-28" value={Math.round((value ?? 60) / 60)}
        onChange={(e) => set(path, Math.min(525_600, Math.max(1, Math.round(Number(e.target.value) || 1) * 60)))} />
      <span className="text-fg-2">hours</span>
    </div>
  );
}

/** Page frame: the section navigation beside the content on wide screens. */
function SettingsLayout({ sectionIds, marks, children }: { sectionIds: SectionId[]; marks: Partial<Record<SectionId, "dirty" | "error">>; children: ReactNode }) {
  const active = useScrollSpy(sectionIds);
  return (
    <div className="mx-auto max-w-3xl lg:grid lg:max-w-5xl lg:grid-cols-[11rem_minmax(0,1fr)] lg:gap-10">
      <SectionNav ids={sectionIds} active={active} marks={marks} desktopOnly />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** The phone version of the navigation, under the page title. */
function SectionNavMobile({ sectionIds, marks }: { sectionIds: SectionId[]; marks: Partial<Record<SectionId, "dirty" | "error">> }) {
  return <SectionNav ids={sectionIds} active={undefined} marks={marks} mobileOnly />;
}

/** The save bar's what-if line: how these settings would have treated the last 7 days of logins. */
function Impact({ result, loading }: { result: SimulationResult | null; loading: boolean }) {
  const [open, setOpen] = useState(false);
  const players = usePlayerNames(result?.changed.slice(0, 8).map((c) => c.event.uuid) ?? []);
  if (!result) return <p className="w-full text-[0.8125rem] text-fg-3">{loading ? "Checking the last 7 days…" : ""}</p>;
  if (result.total === 0) return <p className="w-full text-[0.8125rem] text-fg-3">No logins in the last 7 days to compare with.</p>;
  const diff = result.refusedAfter - result.refusedBefore;
  return (
    <div className="w-full">
      <p className="text-[0.8125rem]">
        <span className="font-medium">With these settings, {num(result.refusedAfter)} of {num(result.total)} logins</span>
        <span className="text-fg-2"> in the last 7 days would have been refused ({diff === 0 ? "no change" : diff > 0 ? `${num(diff)} more` : `${num(-diff)} fewer`}).</span>
        {result.changed.length > 0 && (
          <button type="button" className="ml-2 font-medium underline decoration-line-strong underline-offset-4 hover:text-fg" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Who?"}
          </button>
        )}
      </p>
      {open && (
        <ul className="ph-no-capture mt-2 max-h-48 divide-y divide-line overflow-y-auto rounded-lg border border-line bg-surface text-[0.8125rem]">
          {result.changed.slice(0, 8).map((c) => (
            <li key={c.event.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0 truncate">{(c.event.uuid && players.get(c.event.uuid)) || <span className="mono">{c.event.ip}</span>}</span>
              <span className={`shrink-0 ${c.after === "refused" ? "text-danger-text" : "text-accent-text"}`}>{c.after === "refused" ? "would be refused" : "would get in"} · {c.why}</span>
            </li>
          ))}
          {result.changed.length > 8 && <li className="px-3 py-2 text-fg-3">and {num(result.changed.length - 8)} more</li>}
        </ul>
      )}
      {result.unknown > 0 && <p className="mt-1 text-[0.75rem] text-fg-3">Estimate: a newly enabled service was not asked for {num(result.unknown)} of these logins.</p>}
    </div>
  );
}
